'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HelpCircle } from 'lucide-react';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { useProjectGoalsGuide } from '@/features/project/hooks/use-goals-guide';
import { GoalsBriefGuide } from '@/features/project/ui/GoalsBriefGuide';
import { GoalsQualityIndicator } from '@/features/project/ui/GoalsQualityIndicator';
import { GoalsEvaluationFeedback } from '@/features/project/ui/GoalsEvaluationFeedback';
import { FacadeAreaField } from '@/features/project/ui/FacadeAreaField';
import type { useGoalsEditor } from '@/features/project/hooks/use-goals-editor';

interface BriefTextFormProps {
  /** The live editor buffer — owned by the host, which also owns the commit. */
  editor: ReturnType<typeof useGoalsEditor>;
  /**
   * Prefix for the field ids. Page and panel can be mounted at the same time,
   * and duplicate ids would point every label at the first field.
   */
  idPrefix: string;
  isArchived?: boolean;
}

/**
 * The brief's text half: the brief heading (with the guide "?" and the
 * goals-quality signal), the goals editor with the model's feedback under it,
 * the construction region, and the facade area.
 *
 * ONE component for both hosts — the standalone brief page and the brief panel
 * inside render settings — so the two can no longer drift apart in wording,
 * hierarchy or behaviour. The buffer lives in `useGoalsEditor` at the host,
 * which also renders the commit affordance in its own header.
 */
export function BriefTextForm({
  editor,
  idPrefix,
  isArchived = false,
}: BriefTextFormProps) {
  const t = useTranslations('project');
  const { data: goalsGuide } = useProjectGoalsGuide();

  const {
    project,
    goals,
    setGoals,
    region,
    setRegion,
    area,
    setArea,
    unit,
    changeUnit,
    areaChanged,
  } = editor;

  // "How to write a great brief" guide modal — opened from the "?" next to the
  // brief heading (it also carries the "write in your native language" note).
  const [briefGuideOpen, setBriefGuideOpen] = useState(false);
  // Whether the model's brief-feedback card (the tips bubble) is expanded.
  // Toggled by tapping the quality indicator next to the heading. Expanded by
  // default.
  const [briefFeedbackExpanded, setBriefFeedbackExpanded] = useState(true);

  const score = project?.attributes.goals_quality_score ?? null;
  const evaluation = project?.attributes.goals_evaluation ?? null;
  const pending = !!project?.attributes.is_goals_evaluation_pending;
  const hasGoals = goals.trim().length > 0;
  // The model's evaluation card renders only when there's something to say (or
  // it's in flight), directly under the heading.
  const hasFeedback = pending || (evaluation?.suggestions?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Heading + goals textarea — the "write in your native language" note
          lives in the brief guide opened from the "?" icon. */}
      <div className="space-y-2">
        {/* pb-3: small breathing room below the section heading. */}
        <div className="flex items-center gap-2 pb-3">
          <Label
            htmlFor={`${idPrefix}-goals`}
            className="text-card-title text-foreground"
          >
            {t('briefHeading')}
          </Label>
          {goalsGuide && (
            <button
              type="button"
              onClick={() => setBriefGuideOpen(true)}
              aria-label={goalsGuide.guide.trigger}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
          {/* Goals-quality signal, inline next to the heading (moved off the
              page/sheet header so it reads against the brief it describes).
              Tapping it collapses/expands the model's tips card under the
              brief input. */}
          <button
            type="button"
            onClick={() => setBriefFeedbackExpanded((v) => !v)}
            aria-label={t('briefTipsToggle')}
            aria-expanded={briefFeedbackExpanded}
            // Fixed w-5 footprint so the center is stable across the
            // empty-dot / spinner / bars states.
            className="ms-auto flex w-5 justify-center"
          >
            <GoalsQualityIndicator
              score={score}
              hasGoals={hasGoals}
              pending={pending}
              size="sm"
            />
          </button>
        </div>
        <Textarea
          id={`${idPrefix}-goals`}
          value={goals}
          onChange={(e) => setGoals(e.target.value)}
          placeholder={t('projectGoalsPlaceholder')}
          className="min-h-[100px]"
          maxLength={10000}
          disabled={isArchived}
          // Clarity opt-in: the brief is user-authored design intent, not PII —
          // unmask it in session replays (Clarity masks all inputs by default;
          // the dashboard toggle can't override it).
          data-clarity-unmask="true"
        />
        {/* The model's evaluation reflects the whole brief — text AND photos —
            shown as neutral "direct speech" directly under the brief input it
            comments on; collapsible via the quality indicator next to the
            heading. */}
        {hasFeedback && briefFeedbackExpanded && (
          <GoalsEvaluationFeedback
            evaluation={evaluation}
            pending={pending}
            score={score}
          />
        )}
      </div>

      {/* Construction region field with label + help popover */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label
            htmlFor={`${idPrefix}-region`}
            className="text-section-eyebrow text-muted-foreground"
          >
            {t('constructionRegion')}
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <p className="text-form-help text-muted-foreground">
                {t('constructionRegionHelpText')}
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <Input
          id={`${idPrefix}-region`}
          type="text"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          placeholder={t('constructionRegionPlaceholder')}
          maxLength={255}
          disabled={isArchived}
          // Clarity opt-in: construction region is a place name, not PII.
          data-clarity-unmask="true"
        />
      </div>

      {/* Facade area — optional; estimates use it as the authoritative building
          area instead of re-guessing from photos. */}
      <FacadeAreaField
        id={`${idPrefix}-facade-area`}
        value={area}
        unit={unit}
        isEstimated={
          !!project?.attributes.facade_area_is_estimated && !areaChanged
        }
        disabled={isArchived}
        onChange={setArea}
        onUnitChange={changeUnit}
        labelClassName="text-section-eyebrow text-muted-foreground"
      />

      {/* "How to write a great brief" guide modal — opened from the "?" next to
          the brief heading; carries the native-language note. */}
      {goalsGuide && (
        <GoalsBriefGuide
          guide={goalsGuide.guide}
          note={t('goalsLanguageNoticeMessage')}
          open={briefGuideOpen}
          onOpenChange={setBriefGuideOpen}
        />
      )}
    </div>
  );
}
